import { GLOBAL_STEP_ORDER } from '../constants/flowConfig';

/**
 * Build-time escape hatch for the mandatory-apply gate.
 *
 * `VITE_SKIPPABLE_STEPS` is a comma-separated list of steps that may be passed
 * with Next even though they normally require a tool apply (or, for Axes
 * Alignment, a completed alignment). Entries are either the 1-based position in
 * `GLOBAL_STEP_ORDER` or the step id itself:
 *
 *   VITE_SKIPPABLE_STEPS=1,5            -> PREPARING_SCAN, TRIMLINE
 *   VITE_SKIPPABLE_STEPS=TRIMLINE,8     -> TRIMLINE, THICKNESS
 *
 * Step numbers follow GLOBAL_STEP_ORDER:
 *   1 PREPARING_SCAN   2 AXES_ALIGNMENT  3 RECTIFICATION  4 ELONGATION
 *   5 TRIMLINE         6 ML_REDUCTION    7 FLARES         8 THICKNESS
 *   9 LOCKING_MECHANISM 10 VALVE        11 LOGO_PLACEMENT 12 FINALIZATION
 *
 * An overridden step still shows the normal "are you sure you want to skip?"
 * confirmation — it just no longer keeps Next disabled.
 */
export const parseSkippableSteps = (value) => {
  const steps = new Set();
  String(value ?? '')
    .split(',')
    .forEach((raw) => {
      const token = raw.trim();
      if (!token) return;
      if (/^\d+$/.test(token)) {
        const step = GLOBAL_STEP_ORDER[Number(token) - 1];
        if (step) steps.add(step);
        return;
      }
      const id = token.toUpperCase();
      if (GLOBAL_STEP_ORDER.includes(id)) steps.add(id);
    });
  return steps;
};

// Vite inlines env vars at build time, so this never changes at runtime.
const SKIP_OVERRIDDEN_STEPS = parseSkippableSteps(import.meta.env.VITE_SKIPPABLE_STEPS);

/** True when the env allowlist lets this step be passed without applying. */
export const isStepSkipOverridden = (step) => SKIP_OVERRIDDEN_STEPS.has(step);

export default isStepSkipOverridden;
